/**
 * §9 — the Documents panel: three batch cards over one inline document workspace.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * GENERATING AND MOVING ARE TWO DIFFERENT THINGS, AND THIS PANEL DOES THEM IN ORDER
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * `POST …/generate` composes a document from the case record and stores its text. It does not
 * move the document. `POST …/state` moves the document and composes nothing. §9's `Draft
 * Letter` is therefore two calls in that order, and the order matters: a batch that reached
 * `draft` without a body would be a case showing a letter that does not exist, which is
 * §1's whole complaint about the manual workflow.
 *
 * A 422 `hearing_document_not_generatable` stops before the move and prints **the API's own
 * message**, which names what is missing — no Team Manager, no bound Code edition, no charge.
 * That message is written to be read by a TD at a venue, so it is shown rather than
 * translated. Where the missing thing is the Team Manager, §10's addressee, the card offers
 * the field itself: sending the TD to another screen on a match day is how a hearing starts
 * late.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHAT IS NOT DRAWN, AND WHY THAT IS THE HONEST ANSWER
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * §9's output chips read `✓ .doc — Record Copy` and `✓ PDF — Sent To Team Manager`, and §10's
 * and §12's toasts say the same. **Neither file exists and nothing is sent.** There is no
 * `.doc` writer, no PDF pipeline and no outbound mail in this system; what exists is a print
 * stylesheet and the browser's own print dialogue, which will put the document on paper or
 * save it as a PDF the TD then files and sends. So the chips are replaced by a sentence
 * saying exactly that, and the toasts announce the move that was actually made. A tick beside
 * "sent to Team Manager" would be a false receipt on the one screen whose purpose is that a
 * document says only true things.
 *
 * **Which buttons appear is still not decided here.** `cardActions` maps §9's own button
 * names onto moves `HEARING_DOCUMENT_TRANSITIONS` admits and `hearingBatchGate` has not shut;
 * a gated batch prints the gate's own message, verbatim, and offers nothing.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * §12'S SERVICE CAPTURE AND §15'S APPEAL CLOCK
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * Two more things live on these cards, and both are legal rather than editorial.
 *
 * **Service is captured on batches 1 and 3 only.** §11's minutes are filed to the case, not
 * served on anybody, so batch 2 gets a sentence saying so and no control at all — not a
 * disabled one, which would read as "not yet" rather than "not ever".
 *
 * **The appeal clock hangs off batch 3's service and no other.** §12: *"The appeal period runs
 * from service of this decision."* Batch 1's service is worth recording and starts nothing,
 * and there is deliberately no countdown beside it. What the clock shows is
 * `deriveAppealWindow`'s own basis — served, not served, or served with no window configured —
 * and this panel never computes a deadline itself.
 *
 * **A correction's record comes from the case file, not from this panel.** The Technical
 * Delegate's Decision #6 = A (19 Sep 2026; migration 066): each letter's corrections and
 * withdrawals - the time and name replaced, the reason, who and when - arrive on the document as
 * `serviceCorrections` and are handed to `ServiceCapture` as they are, to list as the letter's
 * Service History. The panel's own `service` state below stays what it was: this session's
 * memory of what it read or wrote, which the record now outlives.
 */

import { useEffect, useState } from "react";

import {
  HEARING_BATCHES,
  HEARING_BATCH_SUBTITLES,
  HEARING_BATCH_TITLES,
  HEARING_DOCUMENT_STATE_LABELS,
  type HearingBatch,
  type HearingDocumentState,
  type HearingDocumentStates,
} from "@hockeytd/shared";

import {
  advanceHearingDocument,
  fetchHearingDocument,
  generateHearingDocument,
  putHearingDocumentBody,
  putHearingMinutes,
  recordHearingService,
} from "../../api/client.js";
import type {
  HearingCaseDetailView,
  HearingClockSettings,
  HearingMinutesRequest,
  HearingServiceRequest,
} from "../../api/types.js";
import { AppealClock } from "./AppealClock.js";
import { DocumentWorkspace } from "./DocumentWorkspace.js";
import { MinutesForm } from "./MinutesForm.js";
import { ServiceCapture } from "./ServiceCapture.js";
import { TeamManagerField } from "./TeamManagerField.js";
import {
  OUTPUT_HONESTY_NOTE,
  REDRAFT_RECEIPT,
  TOURNAMENT_TIMEZONE,
  cardActions,
  isClosedCase,
  issuedReceipt,
} from "./documentText.js";
import { printHearingDocument } from "./hearingPrint.js";
import { SERVICE_NOT_ON_MINUTES, serviceWording } from "./serviceText.js";

/** Re-exported for the page and the tests: §9's card buttons, and its gate. */
export { cardActions } from "./documentText.js";

function asError(cause: unknown): Error {
  return cause instanceof Error ? cause : new Error("The request failed.");
}

/** What one served letter's record says, once this panel has actually read or written it. */
interface ServiceRecord {
  servedAt: string | null;
  servedOn: string | null;
  /** The instant it moved FROM, when this session corrected or withdrew it. */
  correctedFrom: string | null;
}

export interface DocumentsPanelProps {
  hearing: HearingCaseDetailView | null;
  /**
   * Configuration §05's two thresholds, or **null when they could not be read**.
   *
   * Null is not "no window is configured". The appeal clock draws those as two different
   * states because they are two different facts with two different remedies, and collapsing
   * them would tell a TD no clock is running when one may be.
   */
  clocks?: HearingClockSettings | null;
  /** Bumped after every successful write, so both of the page's reads refresh together. */
  onChanged: () => void;
}

export function DocumentsPanel({
  hearing,
  clocks = null,
  onChanged,
}: DocumentsPanelProps): React.JSX.Element {
  const [openBatch, setOpenBatch] = useState<HearingBatch | null>(null);
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  /** True when the textarea holds words the server has not been told about. */
  const [dirty, setDirty] = useState(false);
  /**
   * §10's `Print` prints the ISSUED document, not the textarea it was typed in.
   *
   * A textarea prints as a clipped scroll box, and §9's rendering rule exists because the
   * read-only view is the one that is legible on paper. So the primary button saves, moves the
   * document to `issued`, and this flag makes the print dialogue wait for the render that
   * replaces the editor with the document view — the TD then prints exactly the copy the
   * record now holds, which is the whole point of saving before issuing.
   */
  const [printWhenIssued, setPrintWhenIssued] = useState(false);

  /**
   * The move this panel has just made and the page has not read back yet.
   *
   * A state move is two round trips from the screen's point of view — the `POST` that makes
   * it, and the refetch that shows it — and between them the `hearing` prop still describes
   * the world before the press. Without this the workspace would flick from draft to issued
   * and back, and, worse, a letter just drafted would render in the READ-ONLY view for as
   * long as the refetch took, which is precisely the mode §9's rendering rule reserves for
   * documents that have gone out.
   *
   * It is an override and not a second source of truth: the effect below drops each entry the
   * moment the server's own answer agrees with it, so a refetch always wins in the end and a
   * move the server refused can never leave a stale word on the chip.
   */
  const [moved, setMoved] = useState<Partial<Record<HearingBatch, HearingDocumentState>>>({});

  /** Which letter's service form is open. One at a time; null when none is. */
  const [openService, setOpenService] = useState<1 | 3 | null>(null);
  /**
   * What this panel KNOWS about each letter's service, as against what the case projection
   * carries.
   *
   * The case file reports `served_at` and not `served_on` — the recipient belongs to the
   * document, not to the list of three — so the name is null there and must not be drawn as
   * "nobody". An entry appears here only when the panel has read `GET …/documents/:batch` or
   * has just written the record itself, and `recipientKnown` is that entry existing.
   */
  const [service, setService] = useState<Partial<Record<1 | 3, ServiceRecord>>>({});

  const caseId = hearing?.id ?? null;

  // A different case is a different set of documents; nothing about the last one survives.
  useEffect(() => {
    setOpenBatch(null);
    setBody("");
    setError(null);
    setNotice(null);
    setMoved({});
    setDirty(false);
    setPrintWhenIssued(false);
    setOpenService(null);
    setService({});
  }, [caseId]);

  useEffect(() => {
    if (hearing === null) return;
    setMoved((current) => {
      const next = { ...current };
      let dropped = false;
      for (const batch of HEARING_BATCHES) {
        if (next[batch] !== undefined && hearing.documents[batch] === next[batch]) {
          delete next[batch];
          dropped = true;
        }
      }
      return dropped ? next : current;
    });
  }, [hearing]);

  /*
   * `service` is deliberately NOT reconciled against the case record the way `moved` is.
   *
   * `moved` is an optimistic guess at a state the server may refuse, so it is dropped the
   * moment the record agrees and can never leave a stale word on a chip. A `service` entry is
   * the opposite: every one of them is either a document the panel READ or an outcome the
   * route RETURNED, so it is never a guess — and it carries the one thing the case projection
   * has no column for on this screen, `served_on`. Dropping it on a disagreement would also
   * be undecidable: a record that disagrees is either a refetch that has not landed yet or
   * another TD's correction, and those look identical from here. It survives until the case
   * changes, and a fresh case clears it whole.
   */

  const openStateFor = (
    batch: HearingBatch | null,
  ): HearingDocumentState | null =>
    hearing === null || batch === null ? null : (moved[batch] ?? hearing.documents[batch]);

  useEffect(() => {
    if (!printWhenIssued) return;
    if (openBatch === null) return;
    if (openStateFor(openBatch) !== "issued") return;
    setPrintWhenIssued(false);
    /* RUN 93: the same dressed press the workspace's own Print button uses — this path is
       §10's `Print` primary, which issues the document and then prints it, and a document
       printed by that route must come out on the same paper, under the same filename, as one
       printed from the button a minute later. */
    printHearingDocument(openBatch, hearing);
    // `openStateFor` is derived from the two dependencies below and needs no entry of its own.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [printWhenIssued, openBatch, moved, hearing]);

  const documents: HearingDocumentStates | null =
    hearing === null
      ? null
      : {
          1: moved[1] ?? hearing.documents[1],
          2: moved[2] ?? hearing.documents[2],
          3: moved[3] ?? hearing.documents[3],
        };

  const run = (work: () => Promise<void>): void => {
    setBusy(true);
    setError(null);
    work().then(
      () => {
        setBusy(false);
      },
      (cause: unknown) => {
        setBusy(false);
        setError(asError(cause));
      },
    );
  };

  const storedBody = (batch: HearingBatch): string =>
    hearing?.documentDetail.find((document) => document.batch === batch)?.body ?? "";

  /** §9's `Draft Letter` / `Draft Decision`: compose, then move. §11's minutes just open. */
  const generate = (batch: HearingBatch): void => {
    if (caseId === null) return;
    if (batch === 2) {
      setNotice(null);
      setError(null);
      setOpenBatch(2);
      return;
    }
    run(async () => {
      const generated = await generateHearingDocument(caseId, batch);
      await advanceHearingDocument(caseId, batch, "draft");
      setBody(generated.body ?? "");
      setDirty(false);
      setMoved((current) => ({ ...current, [batch]: "draft" }));
      setOpenBatch(batch);
      setNotice(null);
      onChanged();
    });
  };

  /**
   * §10d's `Re-Draft From The Case` — compose again, move nothing. Ruled 2026-09-10.
   *
   * It is the FIRST half of `generate` and deliberately not the second. The document is
   * already `draft`; repeating `advanceHearingDocument(…, "draft")` would ask the lattice for
   * a move it is already at, and the state this panel is trying to preserve is the one it is
   * standing in. So this calls `POST …/generate` alone, which recomposes freely while the
   * document is `pending` or `draft` and which migration 029's trigger refuses over an
   * `issued` one — the case the control is never offered in.
   *
   * The TD's own wording is destroyed here, which is why the control is armed before it is
   * pressed and why the receipt says so rather than announcing a success.
   */
  const redraft = (batch: HearingBatch): void => {
    if (caseId === null) return;
    run(async () => {
      const generated = await generateHearingDocument(caseId, batch);
      setBody(generated.body ?? "");
      setDirty(false);
      setNotice(REDRAFT_RECEIPT);
      onChanged();
    });
  };

  /** `Continue Draft`, `Open Letter`, `Open Minutes`, `Open Decision` — no move at all. */
  const open = (batch: HearingBatch): void => {
    if (caseId === null) return;
    setNotice(null);
    setDirty(false);
    const known = storedBody(batch);
    if (known !== "") {
      setBody(known);
      setError(null);
      setOpenBatch(batch);
      return;
    }
    run(async () => {
      const document = await fetchHearingDocument(caseId, batch);
      setBody(document.body ?? "");
      setOpenBatch(batch);
    });
  };

  const move = (batch: HearingBatch, to: HearingDocumentState, receipt: string | null): void => {
    if (caseId === null) return;
    run(async () => {
      await advanceHearingDocument(caseId, batch, to);
      setMoved((current) => ({ ...current, [batch]: to }));
      setNotice(receipt);
      onChanged();
    });
  };

  /** §9's middle clause — the TD's edit, stored, with nothing else moved. */
  const saveDraft = (batch: HearingBatch): void => {
    if (caseId === null) return;
    run(async () => {
      const saved = await putHearingDocumentBody(caseId, batch, body);
      setBody(saved.body ?? body);
      setDirty(false);
      setNotice("Draft Saved · The stored copy is now your wording.");
      onChanged();
    });
  };

  /**
   * §10's `Print` and §12's `Save`: store the wording, THEN issue it.
   *
   * The order is the requirement. §10's banner promises *"the TD's wording is the wording that
   * goes out"*, so a document that reached `issued` carrying the generator's draft while the
   * TD's amendments existed only on screen would be two versions of one letter with no way to
   * tell from the record which was served — §1's defect class exactly. If the save fails
   * nothing else runs: `run` surfaces the refusal, the document stays `draft`, and no print
   * dialogue opens on a copy the record does not hold.
   */
  const issue = (batch: HearingBatch, caseNumber: string): void => {
    if (caseId === null) return;
    run(async () => {
      const saved = await putHearingDocumentBody(caseId, batch, body);
      setBody(saved.body ?? body);
      setDirty(false);
      await advanceHearingDocument(caseId, batch, "issued");
      setMoved((current) => ({ ...current, [batch]: "issued" }));
      setNotice(issuedReceipt(batch, caseNumber));
      if (batch === 1) setPrintWhenIssued(true);
      onChanged();
    });
  };

  /* ── §12's proof of service ──────────────────────────────────────────────── */

  /** What the case record says was served, unless this panel holds something fresher. */
  const recordedServedAt = (batch: 1 | 3): string | null => {
    const known = service[batch];
    if (known !== undefined) return known.servedAt;
    return hearing?.documentDetail.find((document) => document.batch === batch)?.servedAt ?? null;
  };

  /**
   * Whether the case file came from an API that keeps a correction's reason and old time
   * (migration 066) - which it says by carrying `serviceCorrections` on the letter at all, empty or
   * not. `infra/deploy.sh` ships this bundle before it restarts the API, and a rollback keeps this
   * bundle over an older API for as long as nobody redeploys; while the field is absent, nothing on
   * the panel may promise that anything is kept (`serviceWording`).
   */
  const historyKeptOn = (batch: 1 | 3): boolean =>
    hearing?.documentDetail.find((document) => document.batch === batch)?.serviceCorrections !==
    undefined;

  /**
   * Open the service form, reading the document's own record first.
   *
   * The read is what supplies `served_on`, which the case projection does not carry. It is
   * done on OPEN rather than on render so that a case with three cards does not fire three
   * document reads to fill in a name most of them have not got.
   */
  const openServiceForm = (batch: 1 | 3): void => {
    if (caseId === null) return;
    setNotice(null);
    if (recordedServedAt(batch) === null || service[batch] !== undefined) {
      setError(null);
      setOpenService(batch);
      return;
    }
    run(async () => {
      const document = await fetchHearingDocument(caseId, batch);
      setService((current) => ({
        ...current,
        [batch]: {
          servedAt: document.servedAt,
          servedOn: document.servedOn,
          correctedFrom: current[batch]?.correctedFrom ?? null,
        },
      }));
      setOpenService(batch);
    });
  };

  /**
   * Record, correct or withdraw service.
   *
   * The panel composes nothing: `ServiceCapture` has already turned the typed date and time
   * into the offset-bearing instant the route demands, and a 422 `hearing_service_refused`
   * arrives with a message written to be shown to the TD — service on the minutes, service
   * before issue, service in the future, a correction with no reason — which `run` puts
   * straight into the failure banner rather than translating.
   */
  const submitService = (batch: 1 | 3, input: HearingServiceRequest): void => {
    if (caseId === null) return;
    run(async () => {
      const outcome = await recordHearingService(caseId, batch, input);
      setService((current) => ({
        ...current,
        [batch]: {
          servedAt: outcome.servedAt,
          servedOn: outcome.servedOn,
          // A corrected record must be capable of saying it was corrected, and of saying what
          // it moved FROM. `previousServedAt` is the API's own answer to that and is kept.
          correctedFrom: outcome.wasCorrection ? outcome.previousServedAt : null,
        },
      }));
      setOpenService(null);
      // Both correction notices used to say the case history kept the claim and the reason,
      // before anything did. Since migration 066 it does, and they say where it is shown - when
      // the case file says the API keeps it, and promise nothing when it does not.
      const wording = serviceWording(historyKeptOn(batch));
      setNotice(
        outcome.servedAt === null
          ? `Service Withdrawn · ${wording.withdrawnNotice}`
          : outcome.wasCorrection
            ? `Service Corrected · ${wording.correctedNotice}`
            : "Service Recorded · Logged against this document.",
      );
      onChanged();
    });
  };

  const submitMinutes = (input: HearingMinutesRequest): void => {
    if (caseId === null || hearing === null) return;
    run(async () => {
      await putHearingMinutes(caseId, input);
      const generated = await generateHearingDocument(caseId, 2);
      await advanceHearingDocument(caseId, 2, "filed");
      setMoved((current) => ({ ...current, 2: "filed" }));
      setBody(generated.body ?? "");
      setNotice(issuedReceipt(2, hearing.caseNumber));
      onChanged();
    });
  };

  const openState: HearingDocumentState | null =
    documents === null || openBatch === null ? null : documents[openBatch];

  return (
    <section
      className="panel panel--d1"
      aria-labelledby="documentsTitle"
      style={{ marginTop: 18 }}
      data-howto="documents"
    >
      <div className="panel__head">
        <h3 className="panel__title" id="documentsTitle">
          Documents
        </h3>
        <span className="panel__note" data-testid="documentsNote">
          {hearing === null
            ? "Three Batches · Hearing Letter · Minutes · Decision Letter"
            : `Case ${hearing.caseNumber} · Hearing Letter · Minutes · Decision Letter`}
        </span>
        <span className="spacer" />
        <span className="panel__note">Every Output Carries The Competition Branding</span>
      </div>

      {error === null ? null : (
        <div className="failbanner" role="alert" data-testid="documentError">
          <span className="failbanner__label">Not Done</span>
          <span className="failbanner__msg">{error.message}</span>
        </div>
      )}

      {notice === null ? null : (
        <div className="warnnote warnnote--advisory" role="status" data-testid="documentNotice">
          <span className="warnnote__kind">Recorded</span>
          <span className="warnnote__msg">{notice}</span>
        </div>
      )}

      <div className="statgrid" data-testid="batchCards">
        {HEARING_BATCHES.map((batch) => {
          const state = documents === null ? null : documents[batch];
          const offered =
            documents === null ? { actions: [], gate: null } : cardActions(batch, documents);

          return (
            <div className="card" key={batch} data-testid={`batch-${String(batch)}`}>
              <div className="card__head">
                <span className="card__kicker">Batch {batch}</span>
                <span className="statuspill" data-testid={`batchState-${String(batch)}`}>
                  <span className="sdot" aria-hidden="true" />
                  {state === null ? "—" : HEARING_DOCUMENT_STATE_LABELS[state]}
                </span>
              </div>
              <b>{HEARING_BATCH_TITLES[batch]}</b>
              <span className="card__note">{HEARING_BATCH_SUBTITLES[batch]}</span>

              {hearing === null ? (
                <span className="card__body">Open a case to draft this batch.</span>
              ) : batch !== 1 && documents !== null && isClosedCase(documents) ? (
                <span className="card__body" data-testid={`batchClosed-${String(batch)}`}>
                  No document is required. The case was closed as an informal enquiry.
                </span>
              ) : offered.actions.length === 0 ? (
                <span className="card__body" data-testid={`batchGate-${String(batch)}`}>
                  {offered.gate ?? "No move is available from this state."}
                </span>
              ) : (
                <div className="tagpal__chips">
                  {offered.actions.map((action) => (
                    <button
                      key={action.label}
                      type="button"
                      className={`gbtn${action.solid ? " gbtn--solid" : ""}`}
                      disabled={busy}
                      onClick={() => {
                        if (action.kind === "generate") generate(batch);
                        else if (action.kind === "open") open(batch);
                        else move(batch, "not_required", null);
                      }}
                    >
                      {busy ? "Working…" : action.label}
                    </button>
                  ))}
                </div>
              )}

              {/* §12's service capture, on the two LETTERS only. §11's minutes are filed to
                  the case and served on nobody, so batch 2 says so and draws no control. */}
              {hearing === null || documents === null || isClosedCase(documents) ? null : batch ===
                2 ? (
                <span className="card__note" data-testid="serviceNotOnMinutes">
                  {SERVICE_NOT_ON_MINUTES}
                </span>
              ) : (
                <ServiceCapture
                  batch={batch}
                  state={documents[batch]}
                  servedAt={recordedServedAt(batch)}
                  issuedAt={
                    hearing.documentDetail.find((document) => document.batch === batch)
                      ?.issuedAt ?? null
                  }
                  servedOn={service[batch]?.servedOn ?? null}
                  recipientKnown={service[batch] !== undefined}
                  correctedFrom={service[batch]?.correctedFrom ?? null}
                  // The record, from the case file (migration 066). Absent from an API without
                  // 066 - for as long as a rollback keeps this bundle over one - which is no
                  // history to draw and nothing to promise; see the field's note in api/types.ts.
                  corrections={
                    hearing.documentDetail.find((document) => document.batch === batch)
                      ?.serviceCorrections ?? []
                  }
                  historyKept={historyKeptOn(batch)}
                  open={openService === batch}
                  onToggle={() => {
                    if (openService === batch) setOpenService(null);
                    else openServiceForm(batch);
                  }}
                  busy={busy}
                  onSubmit={(input) => {
                    submitService(batch, input);
                  }}
                  timezone={TOURNAMENT_TIMEZONE}
                />
              )}

              {/* §15's appeal window — batch 3's service and no other document's. */}
              {batch === 3 && hearing !== null && documents !== null && !isClosedCase(documents) ? (
                <AppealClock
                  servedAt={recordedServedAt(3)}
                  clocks={clocks}
                  timezone={TOURNAMENT_TIMEZONE}
                />
              ) : null}

              {/*
                * §10'S ADDRESSEE, OFFERED AT THE POINT OF REFUSAL.
                *
                * Until run 21B this drew nothing at all, because `teamManagerName` was not on
                * the case projection and `undefined === null` is false — so the one control
                * that answers `Draft Hearing Letter`'s refusal was invisible on every case
                * that needed it. The API now sends the field and this reads it.
                */}
              {batch === 1 && hearing !== null && (hearing.teamManagerName ?? null) === null ? (
                <TeamManagerField
                  caseId={hearing.id}
                  place="batch1"
                  busy={busy}
                  onSaved={onChanged}
                />
              ) : null}
            </div>
          );
        })}
      </div>

      {hearing !== null && openBatch !== null && openState !== null ? (
        openBatch === 2 && openState !== "filed" ? (
          <MinutesForm
            hearing={hearing}
            busy={busy}
            onSubmit={submitMinutes}
            onClose={() => {
              setOpenBatch(null);
            }}
          />
        ) : (
          <DocumentWorkspace
            batch={openBatch}
            state={openState}
            body={body}
            busy={busy}
            onBodyChange={(next) => {
              setBody(next);
              setDirty(true);
            }}
            dirty={dirty}
            onSaveDraft={() => {
              saveDraft(openBatch);
            }}
            onPrimary={() => {
              issue(openBatch, hearing.caseNumber);
            }}
            onReopen={() => {
              setDirty(false);
              move(openBatch, openBatch === 2 ? "pending" : "draft", null);
            }}
            onRedraft={() => {
              redraft(openBatch);
            }}
            onClose={() => {
              setOpenBatch(null);
            }}
            /* RUN 93: the open case, so the saved PDF is named after the case the document
               is about. The panel holds the whole projection; the press reads the two fields
               it needs off it. */
            printCase={hearing}
          />
        )
      ) : null}

      <div className="panel__foot">
        <b>Each batch is generated from the captured fields, edited by the TD, then output.</b>{" "}
        The minutes are filed to the case and kept in case of an appeal. Branding, letterhead
        logos and the full competition name are a per-tournament configuration item and are
        applied to every generated output. {OUTPUT_HONESTY_NOTE}{" "}
        <b>Service is recorded on the two letters, and the appeal window runs from the
        Decision Letter&rsquo;s service alone.</b>{" "}
        No countdown is ever derived from the date a document was issued: where nothing has
        been served, the screen says so instead of counting.
      </div>
    </section>
  );
}
